// ─────────────────────────────────────────────────────────────
// Per-card presentation options.
//
// Every card carries an `options` object describing the chrome
// drawn around its chart — title styling, an accent bar, a caption
// line, a badge, and an optional arrow action. The builder edits
// it, the export payload carries it, and the viewer renders from
// the same values, so a customised card looks identical in both.
//
// Deliberately a fixed, typed shape rather than free HTML: these
// values are rendered into the DOM and an export can arrive from
// another system.
// ─────────────────────────────────────────────────────────────

export const ACCENT_POSITIONS = ['none', 'top', 'left'];
export const TEXT_ALIGNMENTS = ['left', 'center', 'right'];
export const ARROW_POSITIONS = ['footer', 'header'];
export const ARROW_TARGETS = ['route', 'external'];

// Base size the chart builders were originally written against; a card's
// `chart.fontSize` is interpreted relative to it.
export const DEFAULT_CHART_FONT_SIZE = 10;
export const MIN_CHART_FONT_SIZE = 8;
export const MAX_CHART_FONT_SIZE = 28;

export const DEFAULT_CARD_OPTIONS = {
  title: {
    text: '', // blank = use the chart's slice_name
    bold: true,
    italic: false,
    underline: false,
    uppercase: false,
    size: 11,
    color: null,
    align: 'left',
  },
  caption: {
    text: '', // e.g. "AINA contribution: 5,869"
    bold: false,
    italic: false,
    underline: false,
    size: 11,
    color: null,
    align: 'left',
  },
  badge: {
    text: '',
    color: null,
    background: null,
  },
  accent: {
    position: 'none',
    color: '#2563eb',
    thickness: 3,
  },
  arrow: {
    show: false,
    label: '',
    href: '', // in-app path ('/v2/…') or absolute URL
    target: 'route', // 'route' = react-router push, 'external' = new tab
    position: 'footer',
  },
  // Text inside the chart itself — axis labels, table cells, KPI numbers,
  // pie labels. Expressed as a base size that everything scales from, so the
  // relative hierarchy survives.
  chart: {
    fontSize: DEFAULT_CHART_FONT_SIZE,
  },
  card: {
    background: null,
    className: '', // hook for project SCSS
  },
};

const SECTIONS = Object.keys(DEFAULT_CARD_OPTIONS);

// Merge that tolerates partial/legacy option objects without letting
// unknown keys through.
export function normalizeCardOptions(options) {
  if (!options) return DEFAULT_CARD_OPTIONS;
  return SECTIONS.reduce((acc, section) => {
    const defaults = DEFAULT_CARD_OPTIONS[section];
    const incoming = options[section];
    acc[section] = incoming && typeof incoming === 'object' ? { ...defaults, ...incoming } : defaults;
    return acc;
  }, {});
}

// True when the card still looks exactly like an untouched one —
// used to keep exports lean and to show a "customised" marker.
export function isDefaultCardOptions(options) {
  if (!options) return true;
  const normalized = normalizeCardOptions(options);
  return SECTIONS.every(section => Object.keys(DEFAULT_CARD_OPTIONS[section]).every(key => normalized[section][key] === DEFAULT_CARD_OPTIONS[section][key]));
}

// ── Style builders — shared by the builder cards and the viewer ──

export function buildTextStyle(spec, fallbackColor) {
  if (!spec) return undefined;
  return {
    fontWeight: spec.bold ? 600 : 400,
    fontStyle: spec.italic ? 'italic' : 'normal',
    textDecoration: spec.underline ? 'underline' : 'none',
    textTransform: spec.uppercase ? 'uppercase' : 'none',
    fontSize: spec.size || undefined,
    color: spec.color || fallbackColor,
    textAlign: spec.align || 'left',
    letterSpacing: spec.uppercase ? '0.04em' : undefined,
  };
}

export function buildAccentStyle(accent) {
  if (!accent || accent.position === 'none' || !accent.color) return null;
  const thickness = accent.thickness || 3;
  return accent.position === 'top'
    ? { position: 'absolute', top: 0, left: 0, right: 0, height: thickness, background: accent.color, zIndex: 2 }
    : { position: 'absolute', top: 0, bottom: 0, left: 0, width: thickness, background: accent.color, zIndex: 2 };
}

// ── Arrow link handling ──────────────────────────────────────
//
// Hrefs can arrive from an export produced elsewhere, so they are
// validated before they ever reach the DOM: in-app targets must be
// a root-relative path, external ones must be http(s)/mailto. A
// protocol-relative '//evil.com' is rejected as a path, and
// 'javascript:' can never match either branch.

const EXTERNAL_PROTOCOLS = ['http:', 'https:', 'mailto:'];

export function isRoutePath(href) {
  return typeof href === 'string' && href.startsWith('/') && !href.startsWith('//');
}

export function isExternalUrl(href) {
  if (typeof href !== 'string' || !href.trim()) return false;
  try {
    return EXTERNAL_PROTOCOLS.includes(new URL(href, window.location.origin).protocol) && /^[a-z][a-z0-9+.-]*:/i.test(href);
  } catch (e) {
    return false;
  }
}

// Infers the target when none is stored (older exports), otherwise
// honours the stored one — falling back if the href does not match it.
export function resolveArrowLink(arrow) {
  const href = typeof arrow?.href === 'string' ? arrow.href.trim() : '';
  if (!href) return { href: '', target: null };

  const stored = ARROW_TARGETS.includes(arrow?.target) ? arrow.target : null;

  if (stored === 'external' || (!stored && isExternalUrl(href))) {
    return isExternalUrl(href) ? { href, target: 'external' } : { href: '', target: null };
  }

  return isRoutePath(href) ? { href, target: 'route' } : { href: '', target: null };
}
